import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { FileText, Loader2, Plus, Receipt } from 'lucide-react';
import { toast } from 'sonner';
import { client as api, errorMessage } from '../../api/client';
import { money } from '../../lib/money';
import { formatPhone } from '../../lib/phone';
import InvoiceForm from '../../components/InvoiceForm';
import InvoiceTrackerTable from '../../components/InvoiceTrackerTable';
import type { Invoice, Page, UninvoicedPlacement } from '../../types';
import { titleCaseName, titleCaseNameOr } from '../../lib/titleCase';

/**
 * Invoices, as the owner works them.
 *
 * A separate page from /admin/billing because it has a different audience:
 * this one is the owner's, and the worklist below is refused to everybody else
 * by the server as well as by the nav. /admin/billing keeps serving managers
 * and admins exactly as it did — invoicing was deliberately not tightened to
 * make this page feel restricted, because that would have taken it away from
 * them everywhere.
 *
 * The form is imported, not copied. Both screens mount the same InvoiceForm
 * and post to the same endpoint, so the tax figures, the validation and the
 * client-rate snapshot have one implementation between them. Two forms over
 * one endpoint drift, and the figures they disagree about end up on invoices
 * already sent to clients.
 *
 * Placements appear here the moment they exist, before any invoice does. No
 * Invoice row is created by a placement: invoice_number is NOT NULL and unique
 * per company, numbering is sequential and legally significant, and amount
 * must exceed zero — so an auto-created draft would mean inventing a number
 * and a figure for an act nobody performed.
 */
/**
 * Only the identifying column stays frozen.
 *
 *Candidate alone is enough to tell which row is being read, and freezing a
 * second column makes the mobile/position cells slide underneath it. Keeping
 * just one sticky column avoids any partial overlaps while horizontal scroll is
 * in motion.
 */
const STICKY_H = 'sticky left-0 z-20 w-40 bg-gray-50 px-3 py-2.5 text-left font-medium';
const STICKY_C = 'sticky left-0 z-10 w-40 bg-white px-3 py-2 group-hover:bg-gray-50';

/** A money cell. A dash is "not known", which is not the same as zero. */
function Money({ value, className = '' }: { value: string | null; className?: string }) {
  return (
    <td className={`px-3 py-2 text-right tabular-nums ${className || 'text-gray-600'}`}>
      {value === null ? <span className="text-gray-300">—</span> : money(value)}
    </td>
  );
}

export default function InvoicesPage() {
  const queryClient = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  /**
   * The placement being raised against, or null for a blank form.
   *
   * Held rather than passed straight through because the form is seeded from
   * it and also shows its facts read-only — the row is the whole reason the
   * form is not empty.
   */
  const [raising, setRaising] = useState<UninvoicedPlacement | null>(null);
  /**
   * The invoice open in the manage panel — one panel for both jobs.
   *
   * Editing a draft and recording money against a raised invoice were two
   * separate places, which meant two ideas of what an invoice is made of. They
   * are one panel now, and which half is live follows from the status: a draft
   * has editable figures and no payment, a raised invoice has locked figures
   * and a live payment. The server enforces exactly that split, so the panel is
   * showing the rule rather than inventing one.
   */
  const [managing, setManaging] = useState<Invoice | null>(null);

  const invoices = useQuery<Page<Invoice>>({
    queryKey: ['invoices', 'owner'],
    queryFn: async () =>
      (await api.get('/api/v1/admin/billing/invoices', { params: { limit: 200 } })).data,
  });

  const waiting = useQuery<Page<UninvoicedPlacement>>({
    queryKey: ['uninvoiced-placements'],
    queryFn: async () =>
      (
        await api.get('/api/v1/admin/billing/uninvoiced-placements', {
          params: { limit: 200 },
        })
      ).data,
  });

  const create = useMutation({
    mutationFn: async (body: Record<string, unknown>) =>
      (await api.post('/api/v1/admin/billing/invoices', body)).data as Invoice,
    onSuccess: () => {
      toast.success('Invoice created as a draft');
      setShowForm(false);
      // Both lists move: the placement leaves the worklist as the invoice
      // joins the ledger.
      void queryClient.invalidateQueries({ queryKey: ['invoices'] });
      void queryClient.invalidateQueries({ queryKey: ['uninvoiced-placements'] });
    },
    onError: (e) => toast.error(errorMessage(e)),
  });

  const save = useMutation({
    mutationFn: async ({
      id,
      body,
      payment,
    }: {
      id: string;
      body: Record<string, unknown> | null;
      payment: Record<string, unknown> | null;
    }) => {
      if (body) {
        await api.patch(`/api/v1/admin/billing/invoices/${id}`, body);
      }
      if (payment) {
        await api.post(`/api/v1/admin/billing/invoices/${id}/payments`, payment);
      }
      return { patched: Boolean(body), paid: Boolean(payment) };
    },
    onSuccess: (result) => {
      toast.success(
        result.paid
          ? result.patched
            ? 'Draft updated and payment recorded'
            : 'Payment recorded'
          : 'Draft updated',
      );
      setManaging(null);
      // The status is derived from what has been settled, so the row has to be
      // refetched rather than adjusted here — nothing on the client decides it.
      void queryClient.invalidateQueries({ queryKey: ['invoices'] });
      void queryClient.invalidateQueries({ queryKey: ['uninvoiced-placements'] });
    },
    onError: (e) => toast.error(errorMessage(e)),
  });

  /**
   * Draft to raised — the step that was missing from this page entirely.
   *
   * The endpoint and the rule both already existed; what did not exist was any
   * way to reach them from here. /admin/billing has had a Raise button in its
   * invoice drawer all along, so the four drafts sitting in this table were
   * never actually stranded — but an Owner-only invoices page that cannot
   * raise an invoice is not a finished page.
   *
   * Nothing about what a draft means has changed. This calls the same
   * CompanyAdmin endpoint, which is what freezes the snapshot fields and
   * writes the INVOICE_RAISED audit entry.
   */
  const raise = useMutation({
    mutationFn: async (id: string) =>
      (await api.post(`/api/v1/admin/billing/invoices/${id}/raise`)).data as Invoice,
    onSuccess: (invoice) => {
      toast.success(`${invoice.invoiceNumber} raised`);
      // Left open, and now showing its raised self: the figures lock and the
      // payment fields come alive, which is the whole point of having pressed
      // the button. Closing here would hide the result of the action.
      setManaging(invoice);
      void queryClient.invalidateQueries({ queryKey: ['invoices'] });
    },
    onError: (e) => toast.error(errorMessage(e)),
  });

  const pending = waiting.data?.items ?? [];
  const rows = invoices.data?.items ?? [];

  return (
    <div className="space-y-6">
      <section className="card">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 className="text-sm font-semibold text-gray-900">Invoices</h2>
            <p className="mt-1 max-w-2xl text-xs text-gray-500">
              Every placement appears here as soon as it exists. Confirm the
              tax treatment and raise the invoice when the client is ready to
              be billed — nothing is billed automatically.
            </p>
          </div>
          <button
            type="button"
            className="btn-primary"
            onClick={() => setShowForm((v) => !v)}
          >
            <Plus className="h-4 w-4" />
            {showForm ? 'Close' : 'Raise an invoice'}
          </button>
        </div>
      </section>

      {showForm && (
        /* requirePlacement: this is the placement-billing screen, so an
           invoice raised here names the placement it bills for. The general
           path on /admin/billing keeps it optional, because not everything
           billed is a placement fee. */
        <InvoiceForm
          requirePlacement
          submitting={create.isPending}
          onSubmit={(body) => create.mutate(body)}
        />
      )}

      <section className="space-y-2">
        <h3 className="text-sm font-semibold text-gray-900">
          Ready To Invoice
          {pending.length > 0 && (
            <span className="ml-2 rounded bg-amber-100 px-2 py-0.5 text-xs font-normal text-amber-800">
              {pending.length}
            </span>
          )}
        </h3>
        <p className="text-xs text-gray-500">
          Placements with no invoice raised against them yet. Nothing is owed
          on these until one is.
        </p>

        {waiting.isLoading ? (
          <div className="flex justify-center py-6 text-gray-400">
            <Loader2 className="h-5 w-5 animate-spin" />
          </div>
        ) : pending.length === 0 ? (
          <div className="card text-center">
            <Receipt className="mx-auto h-6 w-6 text-gray-300" />
            <p className="mt-2 text-sm text-gray-600">
              Every placement has been invoiced.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto rounded-xl bg-white ring-1 ring-gray-200">
            <table className="w-full text-left text-xs">
              <thead className="sticky top-0 z-30 border-b border-gray-200 bg-gray-50 text-gray-500">
                <tr>
                  <th className={STICKY_H}>Candidate</th>
                  <th className="px-3 py-2.5 font-medium">Client</th>
                  <th className="px-3 py-2.5 font-medium">Mobile</th>
                  <th className="px-3 py-2.5 font-medium">Position</th>
                  <th className="px-3 py-2.5 font-medium">D.O.J</th>
                  <th className="px-3 py-2.5 font-medium">Location</th>
                  <th className="px-3 py-2.5 text-right font-medium">CTC</th>
                  <th className="px-3 py-2.5 text-right font-medium">Net</th>
                  <th className="px-3 py-2.5 text-right font-medium">GST</th>
                  <th className="px-3 py-2.5 text-right font-medium">With GST</th>
                  <th className="px-3 py-2.5 text-right font-medium">TDS</th>
                  <th className="px-3 py-2.5 text-right font-medium">After TDS</th>
                  <th className="px-3 py-2.5 font-medium">Recruiter</th>
                  <th className="px-3 py-2.5 font-medium"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {pending.map((row) => (
                  <tr key={row.placementId} className="group hover:bg-gray-50">
                    <td className={`${STICKY_C} font-medium text-gray-900`}>
                      {titleCaseName(row.candidateName) ?? '—'}
                    </td>
                    <td className="px-3 py-2 text-gray-600">{row.clientName ?? '—'}</td>
                    <td className="whitespace-nowrap px-3 py-2 text-gray-600">
                      {formatPhone(row.candidatePhone) ?? '—'}
                    </td>
                    <td className="px-3 py-2 text-gray-600">{row.position ?? '—'}</td>
                    <td className="whitespace-nowrap px-3 py-2 text-gray-600">
                      {row.dateOfJoining ?? '—'}
                    </td>
                    <td className="px-3 py-2 text-gray-600">{row.location ?? '—'}</td>
                    <td className="px-3 py-2 text-right tabular-nums text-gray-600">
                      {row.ctcAnnual ? money(row.ctcAnnual) : '—'}
                    </td>
                    {/* Costed server-side by the invoice's own functions, so
                        what is previewed here is what gets raised. A dash is
                        not zero: it means the figure is not known. */}
                    <Money value={row.netAmount} className="text-gray-900" />
                    <Money value={row.gstAmount} />
                    <Money value={row.withGst} className="text-gray-900" />
                    <Money value={row.tdsAmount} />
                    <Money value={row.afterTds} />
                    <td className="px-3 py-2 text-gray-600">
                      {titleCaseName(row.recruiterName) ?? '—'}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2">
                      {row.canRaise ? (
                        <button
                          type="button"
                          className="btn-ghost text-xs"
                          onClick={() => setRaising(row)}
                        >
                          Raise
                        </button>
                      ) : (
                        /* The reason in place of the action, not a greyed
                           button somebody has to guess at. The server refuses
                           it too — Invoice.amount must exceed zero. */
                        <span className="text-xs text-amber-700">
                          {row.blockedReason}
                        </span>
                      )}
                      {row.taxWarning && (
                        <span className="ml-2 text-xs text-amber-700">
                          {row.taxWarning}
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section className="space-y-2">
        <h3 className="text-sm font-semibold text-gray-900">Raised</h3>
        {invoices.isLoading ? (
          <div className="flex justify-center py-6 text-gray-400">
            <Loader2 className="h-5 w-5 animate-spin" />
          </div>
        ) : (
          /* The same table /admin/billing shows in its tracker view. */
          <InvoiceTrackerTable
            invoices={rows}
            /* Was a no-op, which is what made the raise step unreachable
               from this page: every route into the panel ran through a button
               in a specific cell, and a draft's cells offered none. */
            onOpen={setManaging}
            onRecordPayment={setManaging}
            onEdit={setManaging}
          />
        )}
      </section>

      {raising && (
        /* Confirm-and-save, not re-entry. Everything the placement already
           knows is either seeded into the form or shown beside it, so what is
           left to type is the invoice number — which nothing can supply.

           The snapshot block is read-only on purpose: those fields are copied
           onto the invoice server-side when it is raised, and a typed-over
           value here would put an invoice at odds with the placement it
           bills for. */
        <div className="fixed inset-0 z-40 flex items-start justify-center overflow-y-auto bg-black/30 p-4">
          <div className="w-full max-w-2xl">
            <InvoiceForm
              key={raising.placementId}
              requirePlacement
              title={`Raise an Invoice — ${titleCaseNameOr(raising.candidateName, 'placement')}`}
              onClose={() => setRaising(null)}
              submitLabel="Create draft"
              submitting={create.isPending}
              snapshot={{
                candidateName: raising.candidateName,
                candidatePhone: raising.candidatePhone,
                position: raising.position,
                dateOfJoining: raising.dateOfJoining,
                location: raising.location,
                ctcAnnual: raising.ctcAnnual,
                recruiterName: raising.recruiterName,
                feeBasis: raising.feeBasis,
                feePercent: raising.feePercent,
              }}
              snapshotNote="Copied onto the invoice when it is raised. Correct these on the placement, not here."
              initial={{
                clientId: raising.clientId,
                placementId: raising.placementId,
                // The agreed fee and the GST on it, both costed server-side by
                // the functions the invoice itself uses. Editable, because a
                // guarantee period or a part-billing is a human decision.
                amount: raising.netAmount ?? '',
                taxAmount: raising.gstAmount ?? '0.00',
              }}
              onSubmit={(body) => create.mutate(body)}
            />
            <div className="mt-2 flex justify-end">
              <button type="button" className="btn-ghost" onClick={() => setRaising(null)}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {managing && (
        /* The same form that raises one, seeded — plus the payment fields, so
           correcting a draft and receipting a raised invoice are one place.

           Which half is live is read off the status rather than chosen here.
           update_invoice accepts only a draft and record_payment refuses one,
           so a panel that offered both at once would be offering something the
           server would reject either way. */
        <div className="fixed inset-0 z-40 flex items-start justify-center overflow-y-auto bg-black/30 p-4">
          <div className="w-full max-w-2xl">
            <InvoiceForm
              key={managing.id}
              title={
                managing.status === 'draft'
                  ? `Draft ${managing.invoiceNumber}`
                  : `Invoice ${managing.invoiceNumber}`
              }
              onClose={() => setManaging(null)}
              submitLabel={managing.status === 'draft' ? 'Save draft' : 'Record payment'}
              submitting={save.isPending}
              figuresLocked={managing.status !== 'draft'}
              lockReason={
                'This invoice has been raised and the client holds these figures.'
                + ' Cancel it to correct them.'
              }
              snapshot={{
                candidateName: managing.candidateName,
                candidatePhone: managing.candidatePhone,
                position: managing.position,
                dateOfJoining: managing.dateOfJoining,
                location: managing.placementLocation,
                ctcAnnual: managing.ctcAnnual,
                recruiterName: managing.recruiterName,
                feeBasis: managing.feeBasis,
                feePercent: managing.feePercent,
              }}
              snapshotNote={
                managing.status === 'draft'
                  ? 'Read from the placement, and frozen onto this invoice when it is raised.'
                  : 'Frozen when this invoice was raised. It keeps what was true then, even if the placement has moved since.'
              }
              payment={{
                outstanding: managing.outstanding,
                invoiceDate: managing.invoiceDate,
                enabled: managing.status !== 'draft' && managing.status !== 'cancelled',
                disabledReason:
                  managing.status === 'cancelled'
                    ? 'This invoice is cancelled and cannot take a payment.'
                    : 'This invoice is still a draft. Raise it before recording a payment.',
              }}
              initial={{
                clientId: managing.clientId,
                placementId: managing.placementId ?? '',
                invoiceNumber: managing.invoiceNumber,
                invoiceDate: managing.invoiceDate,
                dueDate: managing.dueDate ?? '',
                amount: managing.amount,
                taxAmount: managing.taxAmount,
                notes: managing.notes ?? '',
              }}
              onSubmit={(body, payment) =>
                save.mutate({
                  id: managing.id,
                  // Only a draft can be patched. Sending the figures for a
                  // raised invoice would be asking for a 409 that the panel
                  // already knows the answer to.
                  body: managing.status === 'draft' ? body : null,
                  payment,
                })
              }
            />
            <div className="mt-2 flex items-center justify-between gap-3">
              {managing.status === 'draft' ? (
                /* The answer to the message the payment section is showing
                   three inches above this. Saying "raise it first" without
                   putting the button in reach is how the page ended up with
                   four drafts and no visible way out. */
                <button
                  type="button"
                  className="btn-primary"
                  disabled={raise.isPending}
                  onClick={() => raise.mutate(managing.id)}
                >
                  {raise.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
                  <FileText className="h-4 w-4" />
                  Raise invoice
                </button>
              ) : (
                <span />
              )}
              <button type="button" className="btn-ghost" onClick={() => setManaging(null)}>
                Close
              </button>
            </div>
            {managing.status === 'draft' && (
              <p className="mt-2 text-xs text-gray-500">
                Raising it makes it a claim on the client. After that the
                figures are fixed and the candidate details are frozen onto it,
                and a correction means cancelling and issuing a new one.
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
